import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ReportsModal from "./helper/reportsModal";
import BroadcastsModal from "./helper/broadcastsModal";
import { getReportedHotspotList } from "./helper/reportedHotspotApi";

const ReportedHotspotPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportsModal, setReportsModal] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedTagName, setSelectedTagName] = useState(null);
  const [broadcastsModal, setBroadcastsModal] = useState(false);
  const [selectedTagIdForBroadcast, setSelectedTagIdForBroadcast] = useState(null);
  const [selectedTagNameForBroadcast, setSelectedTagNameForBroadcast] = useState(null);

  // Filter states
  const [keyword, setKeyword] = useState("");
  const [isSuspended, setIsSuspended] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const keywordTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Tag ID</span>,
        selector: (row) => row.tag_id,
        sortable: true,
        cell: (row) => (
          <span
            style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline" }}
            onClick={() => handleTagIdClick(row)}
          >
            {row.tag_id}
          </span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Tag Name</span>,
        selector: (row) => row.tag_name,
        sortable: true,
        wrap: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Report Count</span>,
        selector: (row) => row.report_count,
        sortable: true,
        cell: (row) => (
          <Badge color="danger" className="px-3 py-2">
            <i className="mdi mdi-flag me-1"></i>
            {row.report_count}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Display Broadcast</span>,
        selector: (row) => row.tag_id,
        sortable: false,
        cell: (row) => (
          <Button
            color="info"
            size="sm"
            onClick={() => handleBroadcastClick(row)}
            className="px-3"
          >
            <i className="mdi mdi-broadcast me-1"></i>
            View Broadcasts
          </Button>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">First Reported At</span>,
        selector: (row) => row.first_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.first_reported_at) return "-";
          const date = new Date(row.first_reported_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Last Reported At</span>,
        selector: (row) => row.last_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.last_reported_at) return "-";
          const date = new Date(row.last_reported_at);
          return date.toLocaleString();
        },
      },
    ],
    []
  );

  const handleTagIdClick = useCallback((row) => {
    setSelectedTagId(row.tag_id);
    setSelectedTagName(row.tag_name);
    setReportsModal(true);
  }, []);

  const handleBroadcastClick = useCallback((row) => {
    setSelectedTagIdForBroadcast(row.tag_id);
    setSelectedTagNameForBroadcast(row.tag_name);
    setBroadcastsModal(true);
  }, []);

  const toggleReportsModal = useCallback(() => {
    setReportsModal((prev) => !prev);
    if (reportsModal) {
      setSelectedTagId(null);
      setSelectedTagName(null);
    }
  }, [reportsModal]);

  // Optimized fetchList with request cancellation
  const fetchList = useCallback(async (debouncedKeyword = null) => {
    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Set loading state
    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        keyword: debouncedKeyword !== null ? debouncedKeyword : (keyword || undefined),
        is_suspended: isSuspended !== "" ? isSuspended : undefined,
      };

      console.log("Filters being sent:", filters);

      const result = await getReportedHotspotList(filters);
      
      // Check if request was aborted or component unmounted
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }
      
      // Extract data and pagination from API response
      const reportedData = result?.data || [];
      const paginationInfo = result?.pagination || {};
      
      setData(reportedData);
      setTotalRows(paginationInfo.totalRecords || 0);
    } catch (error) {
      // Don't log aborted requests
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching reported hotspot list:", error);
        setData([]);
        setTotalRows(0);
      }
    } finally {
      isLoadingRef.current = false;
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      // Clear abort controller if this was the active request
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [keyword, currentPage, limit, isSuspended]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasInitialFetchedRef.current) {
      hasInitialFetchedRef.current = true;
      fetchList();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle rows per page change
  const handlePerRowsChange = useCallback((newPerPage, page) => {
    setCurrentPage(page);
  }, []);

  // Handle keyword change (debounced)
  const handleKeywordChange = useCallback((value) => {
    setKeyword(value);
    setCurrentPage(1);
  }, []);

  // Debounced keyword search (1 second delay)
  useEffect(() => {
    if (!hasInitialFetchedRef.current) {
      return;
    }

    // Clear existing timeout
    if (keywordTimeoutRef.current) {
      clearTimeout(keywordTimeoutRef.current);
    }

    // Set new timeout for 1 second
    keywordTimeoutRef.current = setTimeout(() => {
      fetchList(keyword);
    }, 1000);

    // Cleanup timeout
    return () => {
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]); // Don't include fetchList to avoid unnecessary re-runs

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when isSuspended filter changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1); // Reset to first page when filter changes
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuspended]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
  }, []);

  const clearFilters = useCallback(() => {
    setKeyword("");
    setIsSuspended("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Reported Hotspot" />

          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Reported Hotspot Filters</h4>
              </CardHeader>

              <CardBody>
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label>Keyword Search</Label>
                      <Input
                        type="text"
                        placeholder="Search by tag name or tag ID..."
                        value={keyword}
                        onChange={(e) => handleKeywordChange(e.target.value)}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Is Suspended</Label>
                      <Input
                        type="select"
                        value={isSuspended}
                        onChange={(e) => setIsSuspended(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="true">Suspended</option>
                        <option value="false">Not Suspended</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={4} className="d-flex align-items-end">
                    <Button color="secondary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Reported Hotspots</h4>
              </CardHeader>

              <CardBody>
                <div id="reportedHotspotList">
                  <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    highlightOnHover
                    noDataComponent="No reported hotspots found"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Container>
      </div>

      <ReportsModal
        isOpen={reportsModal}
        toggle={toggleReportsModal}
        tagId={selectedTagId}
        tagName={selectedTagName}
        onUpdate={() => {
          // Optionally refresh the list after suspension
          fetchList();
        }}
      />

      <BroadcastsModal
        isOpen={broadcastsModal}
        toggle={() => {
          setBroadcastsModal(false);
          setSelectedTagIdForBroadcast(null);
          setSelectedTagNameForBroadcast(null);
        }}
        tagId={selectedTagIdForBroadcast}
        tagName={selectedTagNameForBroadcast}
      />
    </React.Fragment>
  );
};

export default ReportedHotspotPage;

